"use client";

import { useState, useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Counter } from "@/components/shared/Counter";
import { Typography } from "@/components/ui/typography";
import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { CollapsibleSection } from "./CollapsibleSection";
import { CheckboxSection } from "./CheckboxSection";
import { PriceRange } from "./PriceRange";
import { HugeiconsIcon } from "@hugeicons/react";
import { FilterHorizontalFreeIcons } from "@hugeicons/core-free-icons";
import { cn } from "@/lib/utils";
import {
  FILTER_PARAM_KEYS,
  SEARCH_PARAMS,
} from "@/constants/api/search-params";

// Import hooks for dynamic data
import { useOrigins } from "@/lib/react-query/queries/references/use-origins";
import { useAccommodationTypes } from "@/lib/react-query/queries/references/use-accommodation-types";
import { useAmenities } from "@/lib/react-query/queries/references/use-amenities";
import { useSearchFilters } from "@/lib/react-query/queries/search/use-search-filters";
import { getOriginSlug } from "@/utils/originUtils";

const DEFAULT_CAPACITY = 2;

// Zod schema — one field per query param the search API accepts
const filterSchema = z.object({
  capacity: z.number().min(1, "تعداد نفرات باید حداقل ۱ باشد"),
  isInstantBookable: z.boolean(),
  hasDiscount: z.boolean(),
  lowerRangePrice: z.string().optional(),
  upperRangePrice: z.string().optional(),
  minOverallRating: z.string().optional(),
  type: z.array(z.string()),
  amenity: z.array(z.string()),
  origin: z.array(z.string()),
});

type FilterFormData = z.infer<typeof filterSchema>;

interface PropertyFilterFormProps {
  className?: string;
}

const PropertyFilterForm: React.FC<PropertyFilterFormProps> = ({
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  // Preferred source: one aggregated call. Falls back to the individual
  // /references/* routes while the backend rolls /search/filters out.
  const { filters, isLoading: isFiltersLoading } = useSearchFilters();

  const { data: originsResponse, isLoading: isOriginsLoading } = useOrigins();
  const {
    data: accommodationTypesResponse,
    isLoading: isAccommodationTypesLoading,
  } = useAccommodationTypes();
  const { data: amenitiesResponse, isLoading: isAmenitiesLoading } =
    useAmenities();

  // Booking origins — the API filters on a slug, which /references/origins
  // doesn't return, so it's derived from the origin's baseUrl.
  const origins = useMemo(() => {
    if (filters.origins.length > 0) return filters.origins;
    if (!originsResponse?.data) return [];

    return originsResponse.data
      .map((origin) => ({
        id: getOriginSlug(origin),
        label: origin.name,
        count: 0,
      }))
      .filter((origin) => !!origin.id);
  }, [filters.origins, originsResponse]);

  // Accommodation types — /references/types returns PascalCase slugs
  // ("Villa") but the search endpoint matches on lowercase ("villa").
  const accommodationTypes = useMemo(() => {
    if (filters.types.length > 0) return filters.types;
    if (!accommodationTypesResponse?.data) return [];

    return accommodationTypesResponse.data.map((type) => ({
      id: type.slug.toLowerCase(),
      label: type.title,
      count: 0,
    }));
  }, [filters.types, accommodationTypesResponse]);

  const amenities = useMemo(() => {
    if (filters.amenities.length > 0) return filters.amenities;
    if (!amenitiesResponse?.data) return [];

    return amenitiesResponse.data.map((amenity) => ({
      id: amenity.slug,
      label:
        amenity.isSubType && amenity.parentTitle
          ? `${amenity.title} (${amenity.parentTitle})`
          : amenity.title,
      count: 0,
    }));
  }, [filters.amenities, amenitiesResponse]);

  const ratingOptions = useMemo(
    () =>
      filters.ratingThresholds.map((threshold) => ({
        id: String(threshold),
        label: `${threshold.toLocaleString("fa-IR")} به بالا`,
        count: 0,
      })),
    [filters.ratingThresholds]
  );

  // Function to get array values from search params
  const getArrayFromSearchParams = (key: string): string[] => {
    const params = searchParams.getAll(key);
    return params.length > 0 ? params : [];
  };

  // Function to get default values from search parameters
  const getDefaultValues = (): FilterFormData => {
    return {
      capacity: parseInt(
        searchParams.get(SEARCH_PARAMS.CAPACITY) || String(DEFAULT_CAPACITY)
      ),
      isInstantBookable:
        searchParams.get(SEARCH_PARAMS.IS_INSTANT_BOOKABLE) === "true",
      hasDiscount: searchParams.get(SEARCH_PARAMS.HAS_DISCOUNT) === "true",
      lowerRangePrice: searchParams.get(SEARCH_PARAMS.LOWER_PRICE) || "",
      upperRangePrice: searchParams.get(SEARCH_PARAMS.UPPER_PRICE) || "",
      minOverallRating: searchParams.get(SEARCH_PARAMS.MIN_RATING) || "",
      type: getArrayFromSearchParams(SEARCH_PARAMS.TYPE),
      amenity: getArrayFromSearchParams(SEARCH_PARAMS.AMENITY),
      origin: getArrayFromSearchParams(SEARCH_PARAMS.ORIGIN),
    };
  };

  const form = useForm<FilterFormData>({
    resolver: zodResolver(filterSchema),
    defaultValues: getDefaultValues(),
  });

  // Reset form values when search params change
  useEffect(() => {
    form.reset(getDefaultValues());
  }, [searchParams]);

  // Function to count active filters
  const getActiveFiltersCount = (): number => {
    const values = form.getValues();
    let count = 0;

    // Count non-default values
    if (values.capacity !== DEFAULT_CAPACITY) count++;
    if (values.isInstantBookable) count++;
    if (values.hasDiscount) count++;
    if (values.lowerRangePrice?.trim()) count++;
    if (values.upperRangePrice?.trim()) count++;
    if (values.minOverallRating?.trim()) count++;
    if (values.type.length > 0) count++;
    if (values.amenity.length > 0) count++;
    if (values.origin.length > 0) count++;

    return count;
  };

  const [activeFiltersCount, setActiveFiltersCount] = useState(
    getActiveFiltersCount()
  );

  // Update filter count when form values change
  useEffect(() => {
    const subscription = form.watch(() => {
      setActiveFiltersCount(getActiveFiltersCount());
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const onSubmit = (data: FilterFormData) => {
    // Start with ALL existing search parameters to preserve everything
    const params = new URLSearchParams(searchParams.toString());

    // Remove only our filter-specific parameters to avoid duplicates
    FILTER_PARAM_KEYS.forEach((param) => params.delete(param));

    // Add filter parameters only if they differ from defaults
    if (data.capacity !== DEFAULT_CAPACITY)
      params.set(SEARCH_PARAMS.CAPACITY, data.capacity.toString());
    if (data.isInstantBookable)
      params.set(SEARCH_PARAMS.IS_INSTANT_BOOKABLE, "true");
    if (data.hasDiscount) params.set(SEARCH_PARAMS.HAS_DISCOUNT, "true");
    if (data.lowerRangePrice?.trim())
      params.set(SEARCH_PARAMS.LOWER_PRICE, data.lowerRangePrice.trim());
    if (data.upperRangePrice?.trim())
      params.set(SEARCH_PARAMS.UPPER_PRICE, data.upperRangePrice.trim());
    if (data.minOverallRating?.trim())
      params.set(SEARCH_PARAMS.MIN_RATING, data.minOverallRating.trim());

    // Handle array parameters
    data.type.forEach((type) => params.append(SEARCH_PARAMS.TYPE, type));
    data.amenity.forEach((amenity) =>
      params.append(SEARCH_PARAMS.AMENITY, amenity)
    );
    data.origin.forEach((origin) =>
      params.append(SEARCH_PARAMS.ORIGIN, origin)
    );

    // Reset page to 1 when applying filters (but preserve all other params)
    params.set(SEARCH_PARAMS.PAGE, "1");

    // Update URL - this preserves ALL existing parameters (checkIn, checkOut, etc.)
    router.push(`${pathname}?${params.toString()}`);
    setIsOpen(false);
  };

  const handleReset = () => {
    // Start with ALL existing search parameters to preserve everything
    const params = new URLSearchParams(searchParams.toString());

    // Remove only our filter-specific parameters
    FILTER_PARAM_KEYS.forEach((param) => params.delete(param));

    // Reset capacity to default and page to 1 (preserve all other params like dates)
    params.set(SEARCH_PARAMS.CAPACITY, String(DEFAULT_CAPACITY));
    params.set(SEARCH_PARAMS.PAGE, "1");

    // This preserves ALL existing parameters (checkIn, checkOut, etc.)
    router.push(`${pathname}?${params.toString()}`);
  };

  const loadingLabel = (
    <div className="py-4 text-center">
      <Typography variant="captionRegular" color="black300">
        در حال بارگذاری...
      </Typography>
    </div>
  );

  const footer = (
    <div className="flex items-center justify-between p-4 border-t border-gray-50 w-full">
      <Button
        type="button"
        variant="ghost"
        onClick={handleReset}
        className="text-sm font-normal"
      >
        حذف فیلترها
      </Button>
      <Button
        variant="secondary"
        type="button"
        onClick={form.handleSubmit(onSubmit)}
        className="flex-1 md:flex-none md:rounded-[13px] px-6 h-10 font-normal text-sm"
      >
        اعمال تغییرات
      </Button>
    </div>
  );

  return (
    <>
      <Button
        variant="pill"
        onClick={() => setIsOpen(true)}
        className={cn("relative", className)}
      >
        <HugeiconsIcon icon={FilterHorizontalFreeIcons} />
        <Typography variant="captionRegular" className="hidden md:block">
          فیلترهای بیشتر
        </Typography>
        {activeFiltersCount > 0 && (
          <div className="absolute -top-2 -left-2 bg-primary text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
            {activeFiltersCount}
          </div>
        )}
      </Button>

      <ResponsiveModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="فیلتر پیشرفته"
        footer={footer}
      >
        <div className="space-y-4 md:max-h-[50vh] overflow-y-auto py-4 md:py-0 px-6 custom-rtl-scrollbar md:mr-3">
          <Controller
            name="capacity"
            control={form.control}
            render={({ field }) => (
              <Counter
                className="p-0"
                label="تعداد نفرات"
                value={field.value}
                onChange={field.onChange}
                min={1}
                max={filters.capacityMax ?? undefined}
              />
            )}
          />
          <hr className="border-gray-50" />
          <Controller
            name="isInstantBookable"
            control={form.control}
            render={({ field }) => (
              <div className="flex items-center justify-between">
                <Typography variant="bodySRegular">فقط آنی و قطعی</Typography>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </div>
            )}
          />
          <hr className="border-gray-50" />
          <Controller
            name="hasDiscount"
            control={form.control}
            render={({ field }) => (
              <div className="flex items-center justify-between">
                <Typography variant="bodySRegular">فقط تخفیف دار</Typography>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </div>
            )}
          />
          <hr className="border-gray-50" />
          <CollapsibleSection title="قیمت برای هر شب" defaultOpen={true}>
            <Controller
              name="lowerRangePrice"
              control={form.control}
              render={({ field: fromField }) => (
                <Controller
                  name="upperRangePrice"
                  control={form.control}
                  render={({ field: toField }) => (
                    <PriceRange
                      fromValue={fromField.value || ""}
                      toValue={toField.value || ""}
                      onFromChange={fromField.onChange}
                      onToChange={toField.onChange}
                    />
                  )}
                />
              )}
            />
          </CollapsibleSection>
          <hr className="border-gray-50" />

          {/* Minimum overall rating */}
          <CollapsibleSection title="حداقل امتیاز" defaultOpen={true}>
            <Controller
              name="minOverallRating"
              control={form.control}
              render={({ field }) => (
                <CheckboxSection
                  options={ratingOptions}
                  // single-select, but reuses the checkbox list UI
                  selectedValues={field.value ? [field.value] : []}
                  onChange={(values: string[]) =>
                    field.onChange(
                      values.find((value) => value !== field.value) || ""
                    )
                  }
                />
              )}
            />
          </CollapsibleSection>
          <hr className="border-gray-50" />

          {/* Amenities */}
          <CollapsibleSection title="ویژگی اقامتگاه" defaultOpen={true}>
            <Controller
              name="amenity"
              control={form.control}
              render={({ field }) => (
                <>
                  {isAmenitiesLoading && isFiltersLoading ? (
                    loadingLabel
                  ) : (
                    <CheckboxSection
                      options={amenities}
                      selectedValues={field.value}
                      onChange={field.onChange}
                    />
                  )}
                </>
              )}
            />
          </CollapsibleSection>
          <hr className="border-gray-50" />

          {/* Accommodation types */}
          <CollapsibleSection title="نوع اقامتگاه" defaultOpen={true}>
            <Controller
              name="type"
              control={form.control}
              render={({ field }) => (
                <>
                  {isAccommodationTypesLoading && isFiltersLoading ? (
                    loadingLabel
                  ) : (
                    <CheckboxSection
                      options={accommodationTypes}
                      selectedValues={field.value}
                      onChange={field.onChange}
                    />
                  )}
                </>
              )}
            />
          </CollapsibleSection>
          <hr className="border-gray-50" />

          {/* Booking origins */}
          <CollapsibleSection title="شرکت‌های اقامتگاهی" defaultOpen={true}>
            <Controller
              name="origin"
              control={form.control}
              render={({ field }) => (
                <>
                  {isOriginsLoading && isFiltersLoading ? (
                    loadingLabel
                  ) : (
                    <CheckboxSection
                      options={origins}
                      selectedValues={field.value}
                      onChange={field.onChange}
                    />
                  )}
                </>
              )}
            />
          </CollapsibleSection>
        </div>
      </ResponsiveModal>
    </>
  );
};

export default PropertyFilterForm;
